/**
 * `<AcceptedUnderwritingBadge>` — Badge showing the accepted-for-underwriting
 * version pointer + a Re-accept affordance (ADR 0019).
 *
 * conversation-model-versioning Phase 5 Session 4 (2026-05-17).
 *
 * ADR 0019 — SHAPE/LABEL/PLACEMENT:
 * - Shape:     Plain `<div>` badge — a DISTINCT element from
 *              `<CurrentVersionIndicator>` (which is a `<Chip>` pill).
 *              NOT a Chip variant/color flip — entirely different component
 *              type with accent treatment (research/phase-5.md §4 verbatim:
 *              `text-accent bg-accent/7 border-accent/13`).
 * - Label:     `ACCEPTED FOR UNDERWRITING · <label>` — different text from
 *              the current indicator.
 * - Placement: ADJACENT to `<AcceptUnderwritingButton>` in the VariablesPanel
 *              header — different screen region from the current indicator
 *              which is inline with the version list. Placement is the PRIMARY
 *              disambiguator (ADR 0019 Decision §2).
 *
 * Renders ONLY when `acceptedUnderwritingVersionId` is non-null (the P3
 * pointer is set by accept only — ADR 0005).
 *
 * Carries a `Re-accept` affordance: re-points via `useAcceptUnderwriting`
 * (P3 accept/re-point one-endpoint — research/phase-5.md §3 + ADR 0013).
 * Re-accept uses the same `useAcceptUnderwriting` hook (POST
 * /api/conversations/:id/underwriting-events) — the server overwrites the
 * pointer on each call.
 *
 * Data source: `useConversations()` list → find by id →
 * `.acceptedUnderwritingVersionId` (Session-2-widened `ConversationItem`).
 * The detail endpoint does NOT expose this pointer — Session-3 established
 * precedent. `useConversationVersions` resolves the label.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useAcceptUnderwriting } from '@/hooks/use-underwriting-events';
import { useConversationVersions } from '@/hooks/use-conversation-versions';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AcceptedUnderwritingBadgeProps {
  /** The id of the version currently pointed to by `accepted_underwriting_version_id`. */
  acceptedUnderwritingVersionId: string | null | undefined;
  /** Active conversation id — threaded to the accept mutation + label resolution. */
  conversationId: string | null;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AcceptedUnderwritingBadge({
  acceptedUnderwritingVersionId,
  conversationId,
  className,
}: AcceptedUnderwritingBadgeProps) {
  const [reacceptOpen, setReacceptOpen] = useState(false);

  const versionsQuery = useConversationVersions(
    acceptedUnderwritingVersionId ? conversationId : null
  );
  const acceptMutation = useAcceptUnderwriting(conversationId);

  // Renders ONLY when the accepted pointer is non-null (ADR 0019).
  if (!acceptedUnderwritingVersionId) return null;

  // Resolve label from the versions list.
  const label =
    versionsQuery.data?.find((v) => v.id === acceptedUnderwritingVersionId)
      ?.label ?? null;

  // Don't render until the label is resolved — avoids showing a raw id.
  if (!label) return null;

  function handleReaccept() {
    if (acceptMutation.isPending) return;
    // Re-accept with the same medium default confidence — the user can change
    // it in the full AcceptUnderwritingButton flow if needed.
    acceptMutation.mutate(
      { confidenceFlag: 'medium' },
      {
        onSuccess: () => {
          setReacceptOpen(false);
          acceptMutation.reset();
        },
      }
    );
  }

  function handleClose() {
    if (acceptMutation.isPending) return;
    acceptMutation.reset();
    setReacceptOpen(false);
  }

  return (
    <>
      {/*
       * DISTINCT element — NOT a Chip variant/color flip (ADR 0019).
       * Accent treatment: text-accent bg-accent/7 border-accent/13
       * (the StatusBadge accent triplet, research/phase-5.md §4 verbatim).
       *
       * Placement: adjacent to AcceptUnderwritingButton in the VariablesPanel
       * header — different screen region from CurrentVersionIndicator which
       * sits inline with the version list (placement = primary disambiguator).
       */}
      <div
        data-testid="accepted-underwriting-badge"
        className={clsx(
          // Shape: flex row badge with accent border — different from the Chip
          // shape used by CurrentVersionIndicator.
          'flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] border',
          'font-mono text-[10px] font-semibold tracking-[0.5px] uppercase',
          // Accent treatment (StatusBadge accent triplet, research §4 verbatim).
          'text-accent bg-accent/7 border-accent/13',
          className
        )}
      >
        {/* Label: "ACCEPTED FOR UNDERWRITING · <label>" — distinct from "CURRENT · <label>" */}
        <span data-testid="accepted-underwriting-badge-label">
          ACCEPTED FOR UNDERWRITING · {label}
        </span>

        {/* Re-accept affordance — re-points via P3 accept/re-point endpoint */}
        <button
          type="button"
          onClick={() => setReacceptOpen(true)}
          data-testid="accepted-underwriting-reaccept-button"
          title="Accept the current model state again to update this pointer"
          className="ml-1 px-1 py-px rounded-[2px] border border-accent/20 text-accent/70 hover:text-accent hover:border-accent/40 hover:bg-accent/10 transition-colors cursor-pointer"
        >
          Re-accept
        </button>
      </div>

      {/* Re-accept confirm modal */}
      <ConfirmModal
        open={reacceptOpen}
        onClose={handleClose}
        onConfirm={handleReaccept}
        title="Re-accept for underwriting"
        confirmLabel={acceptMutation.isPending ? 'Accepting…' : 'Re-accept'}
      >
        <div className="flex flex-col gap-3" data-testid="reaccept-modal-body">
          <p className="text-sm font-body text-text leading-relaxed">
            Accept the current model state for underwriting. This will update
            the accepted version from{' '}
            <span className="font-mono font-semibold text-text-bright">
              {label}
            </span>{' '}
            to your current inputs.
          </p>

          {acceptMutation.isError && (
            <p
              className="text-xs font-body text-danger"
              data-testid="reaccept-error"
            >
              {acceptMutation.error instanceof Error
                ? acceptMutation.error.message
                : 'Something went wrong. Please try again.'}
            </p>
          )}
        </div>
      </ConfirmModal>
    </>
  );
}
